import { useSyncExternalStore } from 'react'
import * as authApi from '@/lib/api/auth'
import { setAccessTokenGetter, setUnauthorizedHandler } from '@/lib/api/client'

/**
 * Session store.
 *
 * - The access token lives in memory only (never localStorage), so an XSS bug can't steal a
 *   long-lived credential. The refresh token is an httpOnly cookie JavaScript can't read.
 * - On load, `bootstrap()` silently exchanges the cookie for an access token.
 * - Tokens refresh 60 s before expiry. Refreshes are serialised across tabs with the Web Locks
 *   API: refresh tokens rotate on every use, and two tabs racing would look like token theft
 *   to the server (which then signs the user out everywhere).
 * - Sign-out in one tab signs out all tabs (BroadcastChannel).
 *
 * state: { status: 'unknown' | 'anonymous' | 'authenticated', user: User | null }
 */
let accessToken = null
let state = { status: 'unknown', user: null }
let refreshTimer = null
let inflight = null
const listeners = new Set()
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('om-auth') : null

const emit = () => listeners.forEach((l) => l())

function setState(next) {
  state = next
  emit()
}

function schedule(expiresIn) {
  clearTimeout(refreshTimer)
  const ms = Math.max((expiresIn - 60) * 1000, 5_000)
  refreshTimer = setTimeout(() => {
    refresh().catch(() => {})
  }, ms)
}

function applySession(session) {
  accessToken = session.access_token
  schedule(session.expires_in)
  setState({ status: 'authenticated', user: session.user })
  return session
}

function clearSession() {
  accessToken = null
  clearTimeout(refreshTimer)
  setState({ status: 'anonymous', user: null })
}

async function withRefreshLock(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('om-auth-refresh', fn)
  }
  return fn()
}

/** Single-flight refresh. Resolves the new session, or null if the user isn't signed in. */
function refresh() {
  inflight ??= withRefreshLock(async () => {
    try {
      const session = await authApi.refreshSession() // null (204) = no session cookie
      if (!session) {
        clearSession()
        return null
      }
      return applySession(session)
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        clearSession()
        return null
      }
      throw err // network error: keep the current state, retry later
    } finally {
      inflight = null
    }
  })
  return inflight
}

setAccessTokenGetter(() => accessToken)
setUnauthorizedHandler(async () => Boolean(await refresh().catch(() => null)))

channel?.addEventListener('message', (event) => {
  if (event.data === 'signed-out') clearSession()
  if (event.data === 'signed-in' && state.status !== 'authenticated') refresh().catch(() => {})
})

export const authStore = {
  getState: () => state,
  subscribe(listener) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },

  /** Restore the session from the refresh cookie (call once on app start). */
  async bootstrap() {
    try {
      await refresh()
    } catch {
      clearSession() // backend unreachable: behave as signed out
    }
  },

  /** After OTP verify (or any response that carries a session). */
  signIn(session) {
    applySession(session)
    channel?.postMessage('signed-in')
  },

  /** Replace the cached user after a profile/role change. */
  setUser(user) {
    if (state.status === 'authenticated') setState({ ...state, user })
  },

  refresh,

  async signOut({ everywhere = false } = {}) {
    try {
      await (everywhere ? authApi.logoutAll() : authApi.logout())
    } catch {
      /* already signed out server-side; clear locally regardless */
    }
    clearSession()
    channel?.postMessage('signed-out')
  },

  /** Local-only reset (account deleted, tests). */
  reset: clearSession,
}

export function useAuth() {
  const snapshot = useSyncExternalStore(authStore.subscribe, authStore.getState)
  return {
    ...snapshot,
    hasRole: (role) => Boolean(snapshot.user?.roles?.includes(role)),
  }
}

/** Where a signed-in user should land by default. */
export function homeFor(user) {
  if (!user || user.needs_role_selection) return '/onboarding/role'
  if (user.roles.includes('admin')) return '/admin'
  if (user.roles.includes('mentor')) return '/mentor'
  return '/mentee'
}

/** Where to send a user right after sign-in. New users go through role selection first,
 * keeping any intent (e.g. /onboarding/role?intent=mentor) or destination. */
export function postSignInPath(user, next) {
  if (user.needs_role_selection) {
    if (next?.startsWith('/onboarding/role')) return next
    return `/onboarding/role${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`
  }
  return next && next !== '/' && !next.startsWith('/onboarding') ? next : homeFor(user)
}

/** Only same-site relative paths (mirrors the backend's open-redirect guard). */
export function safeNext(value) {
  return value && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\') ? value : null
}
